/**
 * Gemerkte Nachrichten — die Auswahl, aus der ein Vergleichssatz entsteht.
 *
 * EINE Stelle für Schlüsselnamen, Serialisierung und Benachrichtigung. Gemerkt
 * wird in der Nachrichtenliste (dort steht der Text, nach dem man entscheidet),
 * gebraucht wird die Liste auf der Reflexionsseite (dort entsteht der Satz).
 * Zwei Seiten, ein Zustand — schriebe jede ihren eigenen `localStorage`-Zugriff,
 * liefen Schlüsselname und Format irgendwann auseinander, und der Fehler zeigte
 * sich als leere Liste, nicht als Absturz.
 *
 * ACHTUNG, gespeichert wird `Message.id` — der Datenbankschlüssel, dieselbe
 * Zahl, die `prompt_eval_messages.message_id` führt und die die Diagnosetabelle
 * in ihrem Tooltip zeigt. NICHT `Message.message_id`: das ist die
 * Discord-Kennung, sie steht im selben Objekt und heißt fast genauso. Wer sie
 * verwechselt, bekommt einen Satz, in dem keine einzige Nachricht gefunden wird
 * — und zwar erst nach dem Einfrieren, wenn er nicht mehr zu ändern ist.
 *
 * ACHTUNG, das `storage`-Ereignis des Browsers feuert im EIGENEN Tab nicht. Für
 * Karte ↔ Streifen in derselben Ansicht braucht es deshalb ein eigenes
 * `CustomEvent`; `abonnieren` hängt an beidem, damit auch ein zweiter offener
 * Tab mitbekommt, was hier passiert.
 *
 * ACHTUNG, kein Store. Es gibt in diesem Projekt kein Pinia, und der Zustand
 * lebt ohnehin in `localStorage` — ein Store wäre eine zweite Kopie davon.
 */

// Dasselbe Namensschema wie die Filter-Persistenz der Nachrichtenliste
// (async_messages_hideReviewed und die anderen sechs). Wer den Speicher des
// Browsers aufräumt, findet alles zusammen.
export const MERK_SCHLUESSEL = "async_messages_merkliste";

// Das Ereignis für den eigenen Tab. Siehe oben, warum `storage` nicht reicht.
export const MERK_EREIGNIS = "merkliste:geaendert";

/**
 * Wie viele Nachrichten höchstens gemerkt werden.
 *
 * ACHTUNG, keine Messung, sondern eine Schranke gegen Unfug: das Schema des
 * Endpunkts lässt 500 zu, und ein Vergleichssatz mit hunderten Nachrichten ist
 * kein Vergleichssatz mehr, sondern ein Bewertungslauf. Die Zahl bleibt
 * darunter, damit die Sperre in der Oberfläche greift, bevor der Dienst mit
 * einem 422 antwortet — der wäre eine Liste von Objekten und käme als
 * "[object Object]" an.
 */
export const MERK_MAX = 200;

function _melden() {
  try {
    window.dispatchEvent(new CustomEvent(MERK_EREIGNIS));
  } catch (e) {
    // Kein Fenster (Test, SSR). Der Zustand steht trotzdem.
  }
}

/**
 * Die gemerkten IDs, in der Reihenfolge, in der sie gemerkt wurden.
 *
 * Ein kaputter Eintrag ist kein Absturz: unlesbares JSON, ein Nicht-Array oder
 * ein Nicht-Zahl-Element ergeben eine leere bzw. bereinigte Liste. Der Speicher
 * gehört dem Browser, nicht uns — er kann von Hand bearbeitet worden sein, und
 * `localStorage` wirft im Privatmodus mancher Browser schon beim Lesen.
 */
export function lesen() {
  let roh = null;
  try {
    roh = window.localStorage.getItem(MERK_SCHLUESSEL);
  } catch (e) {
    return [];
  }
  if (!roh) return [];
  let daten = null;
  try {
    daten = JSON.parse(roh);
  } catch (e) {
    return [];
  }
  if (!Array.isArray(daten)) return [];
  const raus = [];
  for (const wert of daten) {
    const id = Number(wert);
    if (!Number.isInteger(id) || id <= 0) continue;
    if (!raus.includes(id)) raus.push(id);
  }
  return raus;
}

// Ob der letzte Schreibversuch am Speicher gescheitert ist.
//
// ⚠️ Der Grund, dass es diese Auskunft gibt: ohne sie war ein gesperrter
// localStorage (privater Modus, volle Quote) von einer vollen Merkliste nicht
// zu unterscheiden. Der Aufrufer sah „vorher nicht gemerkt, nachher nicht
// gemerkt" und schloss auf die Obergrenze — und meldete bei NULL gemerkten
// Nachrichten, die Liste sei voll. Eine verschwiegene Ursache ist ärgerlich;
// eine durch eine falsche ersetzte ist schlimmer.
let _speicherFehler = false;

/** Ob der letzte Schreibversuch am Speicher scheiterte (nicht an MERK_MAX). */
export function speicherFehler() {
  return _speicherFehler;
}

function _schreiben(ids) {
  try {
    window.localStorage.setItem(MERK_SCHLUESSEL, JSON.stringify(ids));
  } catch (e) {
    // Voller oder gesperrter Speicher. Nicht werfen: eine Merkliste ist eine
    // Bequemlichkeit, und ein Fehler hier dürfte die Bewertung nicht aufhalten.
    _speicherFehler = true;
    return ids;
  }
  _speicherFehler = false;
  _melden();
  return ids;
}

/** Ob diese Nachricht gemerkt ist. */
export function istGemerkt(id) {
  return lesen().includes(Number(id));
}

/** Merken. Gibt die neue Liste zurück; über MERK_MAX wird nichts ergänzt. */
export function merken(id) {
  const zahl = Number(id);
  const ids = lesen();
  if (!Number.isInteger(zahl) || zahl <= 0) return ids;
  if (ids.includes(zahl)) return ids;
  if (ids.length >= MERK_MAX) return ids;
  return _schreiben(ids.concat([zahl]));
}

/** Vergessen. Gibt die neue Liste zurück. */
export function vergessen(id) {
  const zahl = Number(id);
  const ids = lesen();
  if (!ids.includes(zahl)) return ids;
  return _schreiben(ids.filter((x) => x !== zahl));
}

/**
 * Umschalten. Gibt zurück, ob die Nachricht DANACH gemerkt ist.
 *
 * ACHTUNG, der Rückgabewert ist der Zustand danach und nicht der Erfolg: läuft
 * die Liste gegen MERK_MAX, bleibt die Nachricht ungemerkt, und der Aufrufer
 * erfährt es hier — sonst meldete die Karte "gemerkt", während nichts steht.
 */
export function umschalten(id) {
  const zahl = Number(id);
  if (istGemerkt(zahl)) {
    vergessen(zahl);
    return false;
  }
  merken(zahl);
  return istGemerkt(zahl);
}

/** Alles vergessen. */
export function alleVergessen() {
  return _schreiben([]);
}

/**
 * Auf Änderungen hören. Gibt die Abbestellfunktion zurück.
 *
 * Hängt an beiden Wegen: `storage` für einen zweiten Tab, das eigene Ereignis
 * für diesen. Der Rückruf bekommt die frisch gelesene Liste.
 */
export function abonnieren(rueckruf) {
  const eigenes = () => rueckruf(lesen());
  const fremdes = (e) => {
    if (e && e.key && e.key !== MERK_SCHLUESSEL) return;
    rueckruf(lesen());
  };
  window.addEventListener(MERK_EREIGNIS, eigenes);
  window.addEventListener("storage", fremdes);
  return () => {
    window.removeEventListener(MERK_EREIGNIS, eigenes);
    window.removeEventListener("storage", fremdes);
  };
}
